import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ChevronRight, ChevronLeft, Check, Search, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OnboardingSwipeable({ user }) {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showCustomization, setShowCustomization] = useState(null);
  const [direction, setDirection] = useState('forward'); // Track swipe direction
  
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    height: '',
    heightFeet: '',  // NEW: For feet/inches input
    heightInches: '',  // NEW: For feet/inches input
    heightUnit: 'cm',
    weight: '',
    weightUnit: 'kg',
    goalWeight: '',
    goal: '',
    activity_level: '',
    diet_type: '',  // Changed from 'Standard' to '' - no default selection
    halal_required: null,  // Changed from false to null - no default selection
    allergies: [],
    meals_per_day: '',  // Changed from 'coach_decides' to '' - no default selection
    distribution_profile: '',  // Changed from 'balanced' to '' - no default selection
    excluded_categories: [],
    excluded_food_items: [],  // NEW: Specific food items from food bank
    cooking_time_preference: '',  // Changed from 'no_preference' to '' - no default selection
    budget_preference: '',  // Changed from 'no_preference' to '' - no default selection
    meal_prep_style: '',  // Changed from 'mix' to '' - no default selection
    medicalDisclaimerAccepted: false,
    // Phase 1: New fields
    menstruates: null,  // Only shown if gender is Female
    medication_affecting_weight: null  // Changed from false to null - no default selection
  });
  
  // State for food search
  const [foodSearchQuery, setFoodSearchQuery] = useState('');
  const [foodSearchResults, setFoodSearchResults] = useState([]);
  const [foodSearchLoading, setFoodSearchLoading] = useState(false);

  // Calculate total screens dynamically
  const getTotalScreens = () => {
    // Screens: Age(1), Gender(2), Menstruates(3-Female), Medication(4), Height(5), Weight(6), Goal(7), GoalWeight(8), Activity(9), Diet(10), Halal(11), Allergies(12)
    // Then: Meals(13), Disclaimer(18)
    return 18; // Total screens
  };

  const progress = (currentScreen / getTotalScreens()) * 100;

  // Food search function for food bank
  const searchFoodBank = async (query) => {
    if (!query || query.length < 2) {
      setFoodSearchResults([]);
      return;
    }
    
    setFoodSearchLoading(true);
    try {
      const response = await axios.get(`${API}/public/food-search`, {
        params: { search: query, limit: 20 }
      });
      setFoodSearchResults(response.data.foods || []);
    } catch (error) {
      console.error('Food search error:', error);
      setFoodSearchResults([]);
    } finally {
      setFoodSearchLoading(false);
    }
  };

  // Debounced food search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (foodSearchQuery) {
        searchFoodBank(foodSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [foodSearchQuery]);

  // Toggle food item in excluded list
  const toggleExcludedFoodItem = (food) => {
    setFormData(prev => {
      const exists = prev.excluded_food_items.some(f => f.id === food.id);
      if (exists) {
        return {
          ...prev,
          excluded_food_items: prev.excluded_food_items.filter(f => f.id !== food.id)
        };
      } else {
        return {
          ...prev,
          excluded_food_items: [...prev.excluded_food_items, { id: food.id, name: food.name }]
        };
      }
    });
  };

  // Select all visible search results
  const selectAllVisibleFoods = () => {
    setFormData(prev => {
      const currentIds = new Set(prev.excluded_food_items.map(f => f.id));
      const newItems = foodSearchResults
        .filter(f => !currentIds.has(f.id))
        .map(f => ({ id: f.id, name: f.name }));
      return {
        ...prev,
        excluded_food_items: [...prev.excluded_food_items, ...newItems]
      };
    });
  };

  // Map activity level to work_activity and training_activity for backend
  const mapActivityLevel = (level) => {
    const mapping = {
      'sedentary': { work_activity: 'mostly_sitting', training_activity: 'no_exercise' },
      'lightly_active': { work_activity: 'standing_walking', training_activity: '1_2_workouts' },
      'moderately_active': { work_activity: 'standing_walking', training_activity: '3_4_workouts' },
      'very_active': { work_activity: 'physical_job', training_activity: '5_plus_workouts' }
    };
    return mapping[level] || mapping['sedentary'];
  };

  const handleNext = () => {
    // Validation for current screen
    if (!validateCurrentScreen()) return;

    setDirection('forward'); // Set swipe direction

    // Phase 1: Skip menstruates question if not Female (from screen 2 to 4)
    if (currentScreen === 2 && formData.gender !== 'Female') {
      setCurrentScreen(4); // Skip menstruates question
      return;
    }

    // Handle skip logic after meals question
    if (currentScreen === 13) {
      setCurrentScreen(19); // Skip to disclaimer after meals
      return;
    }

    setCurrentScreen(prev => prev + 1);
  };

  // Auto-advance after selecting an option (for single-select questions)
  // Auto-advance after selecting an option (for single-select questions)
  const handleSelectAndAdvance = (field, value, skipToScreen = null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setDirection('forward'); // Always forward when selecting
    
    // Smooth transition delay for better visual feedback
    setTimeout(() => {
      if (skipToScreen) {
        setCurrentScreen(skipToScreen);
      } else if (field === 'gender' && value !== 'Female') {
        // Skip menstruates question if not Female
        setCurrentScreen(4);
      } else if (currentScreen === 12) {
        // After allergies, go to meals
        setCurrentScreen(13);
      } else if (currentScreen === 13) {
        // After meals, skip to disclaimer (screen 18)
        setCurrentScreen(18);
      } else {
        setCurrentScreen(prev => prev + 1);
      }
    }, 350);  // Slightly longer for swipe effect
  };

  // Handle customization choice with auto-advance
  const handleCustomizationChoice = (wantsCustomization) => {
    setShowCustomization(wantsCustomization);
    setDirection('forward');
    setTimeout(() => {
      if (wantsCustomization) {
        setCurrentScreen(14); // Go to first customization screen (meals per day)
      } else {
        setCurrentScreen(20); // Skip to medical disclaimer
      }
    }, 350);
  };

  const handleBack = () => {
    if (currentScreen === 1) return;
    
    setDirection('backward'); // Set swipe direction for back navigation
    
    // Phase 1: Skip menstruates question backwards if not Female (from screen 4 to 2)
    if (currentScreen === 4 && formData.gender !== 'Female') {
      setCurrentScreen(2);
      return;
    }
    
    // Handle customization skip backwards
    if (currentScreen === 20 && showCustomization === false) {
      setCurrentScreen(13);
      return;
    }

    // SKIP SCREENS 16-19 backwards - Hidden for launch (Food Exclusions, Cooking Time, Budget, Meal Prep)
    if (currentScreen === 20 && showCustomization === true) {
      setCurrentScreen(15);
      return;
    }

    setCurrentScreen(prev => prev - 1);
  };

  const validateCurrentScreen = () => {
    switch (currentScreen) {
      case 1:
        // Age validation: 18-99
        if (!formData.age || formData.age < 18 || formData.age > 99) {
          toast.error('Please enter a valid age (18-99)');
          return false;
        }
        return true;
      case 2:
        if (!formData.gender) {
          toast.error('Please select your gender');
          return false;
        }
        return true;
      case 3:
        // Phase 1: Menstruates validation (only for females, skipped for others)
        if (formData.gender === 'Female' && formData.menstruates === null) {
          toast.error('Please select an option');
          return false;
        }
        return true;
      case 4:
        // Phase 1: Medication validation (always required)
        if (formData.medication_affecting_weight === null) {
          toast.error('Please select an option');
          return false;
        }
        return true;
      case 5:
        // Height validation
        if (formData.heightUnit === 'cm') {
          if (!formData.height || formData.height < 140 || formData.height > 220) {
            toast.error('Please enter a valid height (140-220 cm)');
            return false;
          }
        } else {
          // Feet/inches validation
          if (!formData.heightFeet || !formData.heightInches) {
            toast.error('Please enter both feet and inches');
            return false;
          }
          const totalInches = (parseInt(formData.heightFeet) * 12) + parseInt(formData.heightInches);
          const heightCm = totalInches * 2.54;
          if (heightCm < 140 || heightCm > 220) {
            toast.error('Please enter a valid height (4\'7" - 7\'2")');
            return false;
          }
        }
        return true;
      case 6:
        // Weight validation: 45-180 kg (99-397 lbs) — accept both "83.4" and "83,4"
        {
          const w = parseFloat((formData.weight || '').toString().replace(',', '.'));
          if (formData.weightUnit === 'kg') {
            if (!w || w < 45 || w > 180) {
              toast.error('Please enter a valid weight (45-180 kg)');
              return false;
            }
          } else {
            if (!w || w < 99 || w > 397) {
              toast.error('Please enter a valid weight (99-397 lbs)');
              return false;
            }
          }
        }
        return true;
      case 7:
        if (!formData.goal) {
          toast.error('Please select your goal');
          return false;
        }
        return true;
      case 8:
        // Goal weight is optional but if entered must be valid (accept "65,5" too)
        if (formData.goalWeight) {
          const gw = parseFloat(formData.goalWeight.toString().replace(',', '.'));
          if (formData.weightUnit === 'kg' && (gw < 45 || gw > 180)) {
            toast.error('Please enter a valid goal weight (45-180 kg)');
            return false;
          }
          if (formData.weightUnit === 'lbs' && (gw < 99 || gw > 397)) {
            toast.error('Please enter a valid goal weight (99-397 lbs)');
            return false;
          }
        }
        return true;
      case 9:
        if (!formData.activity_level) {
          toast.error('Please select your activity level');
          return false;
        }
        return true;
      case 10:
        if (!formData.diet_type) {
          toast.error('Please select a diet type');
          return false;
        }
        return true;
      case 11:
        // Halal requirement - validation now required since no default
        if (formData.halal_required === null) {
          toast.error('Please select an option');
          return false;
        }
        return true;
      case 12:
        // Allergies - no validation needed (can select none or multiple)
        return true;
      case 18:
        // Medical disclaimer
        if (!formData.medicalDisclaimerAccepted) {
          toast.error('Please accept the medical disclaimer');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const calculateTDEE = () => {
    const { age, gender, height, heightFeet, heightInches, weight, heightUnit, weightUnit, activity_level, goal } = formData;
    
    if (!age || !gender || !weight || !activity_level) {
      return 2000; // Default fallback
    }

    // Convert height to cm
    let heightCm;
    if (heightUnit === 'ft') {
      if (!heightFeet || !heightInches) return 2000;
      const totalInches = (parseInt(heightFeet) * 12) + parseInt(heightInches);
      heightCm = totalInches * 2.54;
    } else {
      if (!height) return 2000;
      heightCm = parseFloat(height);
    }

    // Convert weight to kg
    const weightKg = weightUnit === 'lbs' ? parseFloat(weight) * 0.453592 : parseFloat(weight);

    // Calculate BMR
    let bmr;
    if (gender === 'Male') {
      bmr = 10 * weightKg + 6.25 * heightCm - 5 * parseInt(age) + 5;
    } else {
      bmr = 10 * weightKg + 6.25 * heightCm - 5 * parseInt(age) - 161;
    }

    // Activity multipliers
    const activityMultipliers = {
      'sedentary': 1.25,
      'lightly_active': 1.40,
      'moderately_active': 1.60,
      'very_active': 1.80
    };

    const tdee = bmr * (activityMultipliers[activity_level] || 1.25);

    // Apply goal adjustments
    let targetCalories = tdee;
    if (goal === 'Weight Loss') {
      targetCalories = tdee * 0.85;
    } else if (goal === 'Weight Gain') {
      targetCalories = tdee * 1.15;
    }

    // Safety minimums
    const safetyMinimum = gender === 'Male' ? 1800 : 1500;
    if (targetCalories < safetyMinimum) {
      targetCalories = safetyMinimum;
    }

    return Math.round(targetCalories);
  };

  const handleSubmit = async () => {
    if (!formData.medicalDisclaimerAccepted) {
      toast.error('Please accept the medical disclaimer');
      return;
    }

    setLoading(true);

    try {
      // Convert height and weight to metric (accept comma OR period as decimal separator)
      let heightCm;
      let weightKg = parseFloat((formData.weight || '').toString().replace(',', '.'));
      let goalWeightKg = formData.goalWeight ? parseFloat(formData.goalWeight.toString().replace(',', '.')) : null;

      // Height conversion
      if (formData.heightUnit === 'ft') {
        // Convert feet + inches to cm
        const totalInches = (parseInt(formData.heightFeet) * 12) + parseInt(formData.heightInches);
        heightCm = totalInches * 2.54;
      } else {
        heightCm = parseFloat(formData.height);
      }

      // Weight conversion
      if (formData.weightUnit === 'lbs') {
        weightKg = weightKg * 0.453592;
        if (goalWeightKg) {
          goalWeightKg = goalWeightKg * 0.453592;
        }
      }

      // Map activity level to work/training
      const activityMapping = mapActivityLevel(formData.activity_level);

      // Calculate target calories and round to nearest 100
      const rawTDEE = calculateTDEE();
      const targetCalories = Math.round(rawTDEE / 100) * 100;

      // Get onboarding form ID
      const formsResponse = await axios.get(`${API}/forms`);
      const onboardingForm = formsResponse.data.find(f => f.name === 'Onboarding Form');
      
      if (!onboardingForm) {
        toast.error('Onboarding form not found. Please contact support.');
        setLoading(false);
        return;
      }

      // Prepare submission data
      const submissionData = {
        age: parseInt(formData.age),
        gender: formData.gender,
        weight: Math.round(weightKg),
        height: Math.round(heightCm),
        goal: formData.goal,
        goal_weight: goalWeightKg ? Math.round(goalWeightKg * 10) / 10 : null,
        work_activity: activityMapping.work_activity,
        training_activity: activityMapping.training_activity,
        diet_type: formData.diet_type,
        halal_required: formData.halal_required,  // NEW: Include halal requirement
        allergies: formData.allergies,
        meals_per_day: formData.meals_per_day === 'coach_decides' ? '3' : formData.meals_per_day,
        distribution_profile: formData.distribution_profile,  // NEW: Send distribution profile
        excluded_categories: formData.excluded_categories,
        excluded_food_items: formData.excluded_food_items,  // Specific food items from food bank
        food_dislikes: formData.excluded_food_items.map(f => f.name),  // Also send as food_dislikes for backward compat
        food_dislikes_other: '',
        cooking_time_preference: formData.cooking_time_preference,
        budget_preference: formData.budget_preference,
        meal_prep_style: formData.meal_prep_style,
        spice_tolerance: 'moderate',
        target_calories: targetCalories,
        recommended_calories: targetCalories,
        // Phase 1: New fields
        menstruates: formData.gender === 'Female',  // Assume all females menstruate unless they specify otherwise in check-in
        medication_affecting_weight: false  // Default to false, can be updated later
      };

      // Submit to forms endpoint
      await axios.post(`${API}/forms/${onboardingForm.id}/responses`, submissionData);
      
      // Create initial weight log with starting weight (use yesterday to prevent overwrite)
      try {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const startDate = yesterday.toISOString().split('T')[0];
        
        await axios.post(`${API}/weight-logs`, {
          date: startDate,
          weight: Math.round(weightKg * 10) / 10,  // Round to 1 decimal
          goal_weight: goalWeightKg ? Math.round(goalWeightKg * 10) / 10 : null
        });
      } catch (weightError) {
        console.error('Failed to create initial weight log:', weightError);
      }
      
      // Save goal weight to user profile if provided
      if (goalWeightKg) {
        try {
          await axios.put(`${API}/weight-logs/goal`, { goal_weight: Math.round(goalWeightKg * 10) / 10 });
        } catch (goalError) {
          console.error('Failed to save goal weight:', goalError);
        }
      }
      
      toast.success('Onboarding complete! Your personalized meal plan is ready.');
      
      // Update user in localStorage to reflect onboarding completion
      const storedUser = localStorage.getItem('client_user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        userData.onboarding_completed = true;
        localStorage.setItem('client_user', JSON.stringify(userData));
      }
      
      // Meta Pixel: Track CompleteRegistration (onboarding finished)
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration');
      }
      
      // Navigate to orientation intro (shows once after onboarding)
      // Using window.location for clean reload with updated user state
      window.location.href = '/app/orientation';
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const toggleArrayItem = (array, item) => {
    if (item === 'None') {
      return [];
    }
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    }
    return [...array, item];
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 1:
        return (
          <ScreenCard title="What's your age?">
            <Input
              type="number"
              inputMode="numeric"
              min="18"
              max="99"
              value={formData.age}
              onChange={(e) => setFormData({ ...formData, age: e.target.value })}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && formData.age) {
                  handleNext();
                }
              }}
              className="text-3xl text-center h-20"
              placeholder="25"
              autoFocus
            />
            <p className="text-sm text-gray-500 mt-2 text-center">Ages 18-99</p>
            {/* Continue button moved to bottom navigation */}
          </ScreenCard>
        );

      case 2:
        return (
          <ScreenCard title="What's your gender?">
            <div className="space-y-3">
              {['Male', 'Female'].map(option => (
                <Button
                  key={option}
                  onClick={() => handleSelectAndAdvance('gender', option)}
                  variant={formData.gender === option ? 'default' : 'outline'}
                  className={`w-full h-16 text-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                    formData.gender === option ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                >
                  {formData.gender === option && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                  {option}
                </Button>
              ))}
            </div>
          </ScreenCard>
        );

      case 3:
        // Phase 1: Menstruates question (only shown if Female)
        if (formData.gender !== 'Female') {
          return null; // This screen is skipped
        }
        return (
          <ScreenCard title="Do you menstruate?">
            <div className="space-y-3">
              <Button
                onClick={() => handleSelectAndAdvance('menstruates', true)}
                variant={formData.menstruates === true ? 'default' : 'outline'}
                className={`w-full h-16 text-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                  formData.menstruates === true ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                }`}
              >
                {formData.menstruates === true && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                Yes
              </Button>
              <Button
                onClick={() => handleSelectAndAdvance('menstruates', false)}
                variant={formData.menstruates === false ? 'default' : 'outline'}
                className={`w-full h-16 text-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                  formData.menstruates === false ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                }`}
              >
                {formData.menstruates === false && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                No
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4 text-center">
              This helps us adjust for natural hormonal fluctuations
            </p>
          </ScreenCard>
        );

      case 4:
        // Phase 1: Medication affecting weight
        return (
          <ScreenCard title="Are you on any medication that might affect weight?">
            <div className="space-y-3">
              <Button
                onClick={() => handleSelectAndAdvance('medication_affecting_weight', false)}
                variant={formData.medication_affecting_weight === false ? 'default' : 'outline'}
                className={`w-full h-16 text-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                  formData.medication_affecting_weight === false ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                }`}
              >
                {formData.medication_affecting_weight === false && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                No
              </Button>
              <Button
                onClick={() => handleSelectAndAdvance('medication_affecting_weight', true)}
                variant={formData.medication_affecting_weight === true ? 'default' : 'outline'}
                className={`w-full h-16 text-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                  formData.medication_affecting_weight === true ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                }`}
              >
                {formData.medication_affecting_weight === true && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                Yes
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4 text-center">
              Examples: steroids, antidepressants, birth control, thyroid medication
            </p>
          </ScreenCard>
        );

      case 5:
        return (
          <ScreenCard title="What's your height?">
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  onClick={() => setFormData({ ...formData, heightUnit: 'cm' })}
                  variant={formData.heightUnit === 'cm' ? 'default' : 'outline'}
                  className="flex-1"
                >
                  cm
                </Button>
                <Button
                  onClick={() => setFormData({ ...formData, heightUnit: 'ft' })}
                  variant={formData.heightUnit === 'ft' ? 'default' : 'outline'}
                  className="flex-1"
                >
                  ft
                </Button>
              </div>
              
              {formData.heightUnit === 'cm' ? (
                <>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min="140"
                    max="220"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                    className="text-3xl text-center h-20"
                    placeholder="175"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 text-center">140-220 cm</p>
                </>
              ) : (
                <>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Input
                        type="number"
                        inputMode="numeric"
                        min="4"
                        max="7"
                        value={formData.heightFeet}
                        onChange={(e) => setFormData({ ...formData, heightFeet: e.target.value })}
                        className="text-3xl text-center h-20"
                        placeholder="5"
                        autoFocus
                      />
                      <p className="text-xs text-gray-500 text-center mt-1">feet</p>
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        max="11"
                        value={formData.heightInches}
                        onChange={(e) => setFormData({ ...formData, heightInches: e.target.value })}
                        className="text-3xl text-center h-20"
                        placeholder="9"
                      />
                      <p className="text-xs text-gray-500 text-center mt-1">inches</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 text-center">4'7" - 7'2"</p>
                </>
              )}
              {/* Continue button moved to bottom navigation */}
            </div>
          </ScreenCard>
        );

      case 6:
        return (
          <ScreenCard title="What's your weight?">
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  onClick={() => setFormData({ ...formData, weightUnit: 'kg' })}
                  variant={formData.weightUnit === 'kg' ? 'default' : 'outline'}
                  className="flex-1"
                >
                  kg
                </Button>
                <Button
                  onClick={() => setFormData({ ...formData, weightUnit: 'lbs' })}
                  variant={formData.weightUnit === 'lbs' ? 'default' : 'outline'}
                  className="flex-1"
                >
                  lbs
                </Button>
              </div>
              <Input
                type="text"
                inputMode="decimal"
                pattern="[0-9]+([.,][0-9]+)?"
                value={formData.weight}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.,]/g, '');
                  const firstSep = v.search(/[.,]/);
                  const cleaned = firstSep === -1
                    ? v
                    : v.slice(0, firstSep + 1) + v.slice(firstSep + 1).replace(/[.,]/g, '');
                  setFormData({ ...formData, weight: cleaned });
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && formData.weight) {
                    handleNext();
                  }
                }}
                className="text-3xl text-center h-20"
                placeholder={formData.weightUnit === 'kg' ? '70' : '154'}
                autoFocus
              />
              <p className="text-xs text-gray-500 text-center">
                {formData.weightUnit === 'kg' ? '45-180 kg' : '99-397 lbs'}
              </p>
              {/* Continue button moved to bottom navigation */}
            </div>
          </ScreenCard>
        );

      case 7:
        return (
          <ScreenCard title="What's your main goal?">
            <div className="space-y-3">
              {[
                { value: 'Weight Loss', label: 'Lose weight', emoji: '📉' },
                { value: 'Maintenance', label: 'Maintain weight', emoji: '⚖️' },
                { value: 'Weight Gain', label: 'Gain weight', emoji: '📈' }
              ].map(option => (
                <Button
                  key={option.value}
                  onClick={() => handleSelectAndAdvance('goal', option.value)}
                  variant={formData.goal === option.value ? 'default' : 'outline'}
                  className={`w-full h-16 text-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                    formData.goal === option.value ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                >
                  {formData.goal === option.value && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                  <span className="mr-2">{option.emoji}</span>
                  {option.label}
                </Button>
              ))}
            </div>
          </ScreenCard>
        );

      case 8:
        return (
          <ScreenCard title="What's your goal weight?">
            <div className="space-y-4">
              <p className="text-sm text-gray-500 text-center">
                {formData.goal === 'Maintenance' 
                  ? "Even if you're maintaining, having a target helps track progress."
                  : "This helps us track your progress over time."}
              </p>
              <Input
                type="text"
                inputMode="decimal"
                pattern="[0-9]+([.,][0-9]+)?"
                value={formData.goalWeight}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.,]/g, '');
                  const firstSep = v.search(/[.,]/);
                  const cleaned = firstSep === -1
                    ? v
                    : v.slice(0, firstSep + 1) + v.slice(firstSep + 1).replace(/[.,]/g, '');
                  setFormData({ ...formData, goalWeight: cleaned });
                }}
                className="text-3xl text-center h-20"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && formData.goalWeight) {
                    handleNext();
                  }
                }}
                placeholder={formData.weightUnit === 'kg' ? '65' : '143'}
                autoFocus
              />
              {/* Continue button moved to bottom navigation */}
              <Button
                variant="ghost"
                onClick={() => handleNext()}
                className="w-full text-gray-400"
              >
                Skip for now
              </Button>
            </div>
          </ScreenCard>
        );

      case 9:
        return (
          <ScreenCard title="How active are you?">
            <div className="space-y-3">
              {[
                { value: 'sedentary', label: 'Sedentary', desc: 'Desk job, minimal movement' },
                { value: 'lightly_active', label: 'Lightly active', desc: 'Some walking, light movement' },
                { value: 'moderately_active', label: 'Moderately active', desc: 'On feet often, moderate exercise' },
                { value: 'very_active', label: 'Very active', desc: 'Manual labour or heavy exercise' }
              ].map(option => (
                <Button
                  key={option.value}
                  onClick={() => handleSelectAndAdvance('activity_level', option.value)}
                  variant={formData.activity_level === option.value ? 'default' : 'outline'}
                  className={`w-full h-auto py-4 text-left flex flex-col items-start transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                    formData.activity_level === option.value ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                >
                  {formData.activity_level === option.value && <Check className="absolute right-4 h-5 w-5 animate-in zoom-in duration-200" />}
                  <div className="font-semibold">{option.label}</div>
                  <div className="text-xs text-gray-500">{option.desc}</div>
                </Button>
              ))}
            </div>
          </ScreenCard>
        );

      case 10:
        return (
          <ScreenCard title="Do you follow a specific diet?">
            <div className="space-y-3">
              {['Standard', 'Vegetarian', 'Vegan', 'Pescatarian', 'Keto'].map(option => (
                <Button
                  key={option}
                  onClick={() => handleSelectAndAdvance('diet_type', option)}
                  variant={formData.diet_type === option ? 'default' : 'outline'}
                  className={`w-full h-14 text-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                    formData.diet_type === option ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                >
                  {formData.diet_type === option && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                  {option}
                </Button>
              ))}
            </div>
          </ScreenCard>
        );

      case 11:
        return (
          <ScreenCard title="Dietary Requirements">
            <p className="text-gray-600 mb-4 text-center">Do you have any special dietary requirements?</p>
            <div className="space-y-3">
              {[
                { value: true, label: 'Halal Only', description: 'Exclude all non-halal ingredients' },
                { value: false, label: 'No Restrictions', description: 'All ingredients allowed' }
              ].map(option => (
                <Button
                  key={option.label}
                  onClick={() => handleSelectAndAdvance('halal_required', option.value)}
                  variant={formData.halal_required === option.value ? 'default' : 'outline'}
                  className={`w-full h-auto py-4 flex-col items-start transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                    formData.halal_required === option.value ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                >
                  <div className="flex items-center w-full">
                    {formData.halal_required === option.value && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                    <span className="text-lg font-semibold">{option.label}</span>
                  </div>
                  <span className="text-sm text-gray-500 mt-1">{option.description}</span>
                </Button>
              ))}
            </div>
          </ScreenCard>
        );

      case 12:
        return (
          <ScreenCard title="Any allergies?">
            <div className="flex flex-wrap gap-2">
              {['Gluten', 'Dairy', 'Eggs', 'Nuts', 'Soy', 'Shellfish', 'Fish', 'None'].map(allergy => (
                <Button
                  key={allergy}
                  onClick={() => setFormData({
                    ...formData,
                    allergies: toggleArrayItem(formData.allergies, allergy)
                  })}
                  variant={formData.allergies.includes(allergy) ? 'default' : 'outline'}
                  className="rounded-full"
                >
                  {formData.allergies.includes(allergy) && <Check className="mr-1 h-4 w-4" />}
                  {allergy}
                </Button>
              ))}
            </div>
            {/* Continue button for multi-select */}
            <Button
              onClick={handleNext}
              className="w-full h-14 text-lg mt-6"
            >
              Continue
            </Button>
          </ScreenCard>
        );

      case 13:
        // Meals per day (was screen 14)
        return (
          <ScreenCard title="How many meals per day?">
            <div className="space-y-3">
              {[
                { value: '3', label: '3 meals' },
                { value: '4', label: '4 meals' },
                { value: '5', label: '5 meals' },
                { value: '6', label: '6 meals' },
                { value: 'coach_decides', label: 'Coach decides' }
              ].map(option => (
                <Button
                  key={option.value}
                  onClick={() => handleSelectAndAdvance('meals_per_day', option.value)}
                  variant={formData.meals_per_day === option.value ? 'default' : 'outline'}
                  className={`w-full h-14 text-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                    formData.meals_per_day === option.value ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                >
                  {formData.meals_per_day === option.value && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                  {option.label}
                </Button>
              ))}
            </div>
          </ScreenCard>
        );

      case 14:
        // NEW: Meal Distribution Profile
        return (
          <ScreenCard 
            title="Meal distribution"
            subtitle="What meal do you prefer to eat most of during the day?"
          >
            <div className="space-y-3">
              {[
                { 
                  value: 'balanced', 
                  label: '⚖️ Balanced', 
                  desc: 'Evenly spaced out meals throughout the day' 
                },
                { 
                  value: 'big_breakfast', 
                  label: '🌅 Big Breakfast', 
                  desc: 'Larger breakfast, smaller lunch and dinner' 
                },
                { 
                  value: 'big_lunch', 
                  label: '🌞 Big Lunch', 
                  desc: 'Larger lunch, smaller breakfast and dinner' 
                },
                { 
                  value: 'big_dinner', 
                  label: '🌙 Big Dinner', 
                  desc: 'Larger dinner, smaller breakfast and lunch' 
                }
              ].map(option => (
                <Button
                  key={option.value}
                  onClick={() => handleSelectAndAdvance('distribution_profile', option.value)}
                  variant={formData.distribution_profile === option.value ? 'default' : 'outline'}
                  className={`w-full h-auto py-4 text-left flex flex-col items-start relative transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                    formData.distribution_profile === option.value ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                >
                  {formData.distribution_profile === option.value && <Check className="absolute right-4 top-4 h-5 w-5 animate-in zoom-in duration-200" />}
                  <div className="font-semibold text-base">{option.label}</div>
                  <div className="text-sm text-gray-500 mt-1">{option.desc}</div>
                </Button>
              ))}
            </div>
          </ScreenCard>
        );

      case 15:
        return (
          <ScreenCard
            title="Foods to exclude?"
            subtitle="Choose anything you never want in your meal plan."
          >
            <div className="space-y-4">
              {/* Common food category chips */}
              <div className="flex flex-wrap gap-2">
                {['Chicken', 'Beef', 'Lamb', 'Fish', 'Shellfish', 'Eggs', 'Dairy', 'Tofu', 'Tempeh', 
                  'Lentils', 'Beans', 'Broccoli', 'Cauliflower', 'Mushrooms', 'Courgette', 'Peppers',
                  'Tomatoes', 'Potatoes', 'Sweet Potato', 'Oats', 'Rice', 'Pasta', 'Avocado', 'Nuts', 'None'].map(food => (
                  <Button
                    key={food}
                    onClick={() => setFormData({
                      ...formData,
                      excluded_categories: toggleArrayItem(formData.excluded_categories, food)
                    })}
                    variant={formData.excluded_categories.includes(food) ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full"
                  >
                    {formData.excluded_categories.includes(food) && <Check className="mr-1 h-3 w-3" />}
                    {food}
                  </Button>
                ))}
              </div>

              {/* Search for specific foods */}
              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-gray-600 mb-2">Search for specific foods:</p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    value={foodSearchQuery}
                    onChange={(e) => setFoodSearchQuery(e.target.value)}
                    placeholder="e.g., salmon, quinoa, chickpeas..."
                    className="pl-10"
                  />
                </div>

                {/* Search results */}
                {foodSearchQuery.length >= 2 && (
                  <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                    {foodSearchLoading ? (
                      <p className="p-3 text-sm text-gray-500">Searching...</p>
                    ) : foodSearchResults.length > 0 ? (
                      <>
                        <div className="p-2 border-b bg-gray-50 flex justify-between items-center">
                          <span className="text-xs text-gray-500">{foodSearchResults.length} results</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={selectAllVisibleFoods}
                            className="h-6 text-xs"
                          >
                            Select all visible
                          </Button>
                        </div>
                        {foodSearchResults.map(food => (
                          <div
                            key={food.id}
                            onClick={() => toggleExcludedFoodItem(food)}
                            className={`p-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
                              formData.excluded_food_items.some(f => f.id === food.id) ? 'bg-blue-50' : ''
                            }`}
                          >
                            <span className="text-sm">{food.name}</span>
                            {formData.excluded_food_items.some(f => f.id === food.id) && (
                              <Check className="h-4 w-4 text-blue-600" />
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <p className="p-3 text-sm text-gray-500">No foods found for &quot;{foodSearchQuery}&quot;</p>
                    )}
                  </div>
                )}

                {/* Selected specific foods */}
                {formData.excluded_food_items.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">Selected specific foods:</p>
                    <div className="flex flex-wrap gap-1">
                      {formData.excluded_food_items.map(food => (
                        <span
                          key={food.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                        >
                          {food.name}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-blue-600"
                            onClick={() => toggleExcludedFoodItem(food)}
                          />
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Continue button for multi-select */}
              <Button
                onClick={handleNext}
                className="w-full h-14 text-lg mt-4"
              >
                Continue
              </Button>
            </div>
          </ScreenCard>
        );

      case 16:
        return (
          <ScreenCard title="Preferred cooking time?">
            <div className="space-y-3">
              {[
                { value: 'quick', label: 'Under 20 minutes' },
                { value: 'moderate', label: '20–30 minutes' },
                { value: 'no_preference', label: 'No preference' }
              ].map(option => (
                <Button
                  key={option.value}
                  onClick={() => handleSelectAndAdvance('cooking_time_preference', option.value)}
                  variant={formData.cooking_time_preference === option.value ? 'default' : 'outline'}
                  className={`w-full h-14 text-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                    formData.cooking_time_preference === option.value ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                >
                  {formData.cooking_time_preference === option.value && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                  {option.label}
                </Button>
              ))}
            </div>
          </ScreenCard>
        );

      case 17:
        return (
          <ScreenCard title="Budget preference?">
            <div className="space-y-3">
              {[
                { value: 'budget_friendly', label: 'Budget-friendly' },
                { value: 'moderate', label: 'Moderate' },
                { value: 'premium', label: 'Premium' },
                { value: 'no_preference', label: 'No preference' }
              ].map(option => (
                <Button
                  key={option.value}
                  onClick={() => handleSelectAndAdvance('budget_preference', option.value)}
                  variant={formData.budget_preference === option.value ? 'default' : 'outline'}
                  className={`w-full h-14 text-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${
                    formData.budget_preference === option.value ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                >
                  {formData.budget_preference === option.value && <Check className="mr-2 h-5 w-5 animate-in zoom-in duration-200" />}
                  {option.label}
                </Button>
              ))}
            </div>
          </ScreenCard>
        );

      case 18:
        // Medical disclaimer (final screen)
        return (
          <ScreenCard title="Almost done!">
            <div className="space-y-6">
              <p className="text-gray-600">
                Please confirm you understand this is not medical advice.
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={formData.medicalDisclaimerAccepted}
                  onCheckedChange={(checked) => setFormData({ ...formData, medicalDisclaimerAccepted: checked })}
                />
                <span className="text-sm">
                  I confirm that I understand this meal plan is for general nutrition guidance only and is not a substitute for professional medical advice, diagnosis, or treatment.
                </span>
              </label>
              <Button
                onClick={handleSubmit}
                disabled={!formData.medicalDisclaimerAccepted || loading}
                className="w-full h-14 text-lg"
              >
                {loading ? 'Creating your plan...' : 'Finish 🎉'}
              </Button>
            </div>
          </ScreenCard>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex flex-col">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 z-50">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 pt-8 overflow-hidden">
        <div className="w-full max-w-md">
          <div key={currentScreen} className={`
            ${direction === 'forward' 
              ? 'animate-in slide-in-from-right-8 fade-in duration-500' 
              : 'animate-in slide-in-from-left-8 fade-in duration-500'
            }
          `}>
            {renderScreen()}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-3">
        <Button
          onClick={handleBack}
          variant="outline"
          className="flex-shrink-0"
          disabled={currentScreen === 1}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        {/* Only show Continue button for text input screens (age, height, weight, goal weight) */}
        {/* For other screens, clicking options auto-advances or they have their own button */}
        {(currentScreen === 1 || currentScreen === 5 || currentScreen === 6 || currentScreen === 8) && (
          <Button
            onClick={handleNext}
            className="flex-1"
            disabled={
              (currentScreen === 1 && !formData.age) ||
              (currentScreen === 5 && !formData.height) ||
              (currentScreen === 6 && !formData.weight) ||
              (currentScreen === 8 && !formData.goalWeight)
            }
          >
            Continue
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function ScreenCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
      <div>
        {children}
      </div>
    </div>
  );
}
